import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search, Plus, Eye, FileText, Trash2, Calculator,
  DollarSign, ClipboardList, Calendar,
} from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePermission } from "@/hooks/usePermission";
import { toast } from "sonner";

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

export function CotizacionesACListTab() {
  const [search, setSearch] = useState("");
  const canCreate = usePermission("otac_create");
  const canExport = usePermission("otac_export");
  const canDelete = usePermission("otac_delete");
  const qc = useQueryClient();

  const { data: cotizaciones = [], isLoading } = useQuery({
    queryKey: ["cotizaciones-ac", search],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      return (await api.get("/cotizaciones-ac", { params })).data;
    },
  });

  const downloadPdf = async (id, folio) => {
    try {
      const res = await api.get(`/cotizaciones-ac/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cotizacion_${folio}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch {
      toast.error("Error al descargar PDF");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/cotizaciones-ac/${id}`);
      toast.success("Cotización eliminada");
      qc.invalidateQueries({ queryKey: ["cotizaciones-ac"] });
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-4" data-testid="cotizaciones-ac-list">
      {/* Header + actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar folio o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="cotizaciones-search-input"
            />
          </div>
          <Badge variant="outline" className="py-1 px-3 font-mono tabular-nums shrink-0" data-testid="cotizaciones-count">
            {cotizaciones.length} cotizaciones
          </Badge>
        </div>
        {canCreate && (
          <Button asChild data-testid="cotizaciones-create-button">
            <Link to="/ots-comercial/cotizaciones/new">
              <Plus className="h-4 w-4 mr-1.5" /> Nueva Cotización
            </Link>
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2.5 px-3">Folio</th>
                  <th className="py-2.5 px-3">Cliente</th>
                  <th className="py-2.5 px-3">Contacto</th>
                  <th className="py-2.5 px-3">Fecha</th>
                  <th className="py-2.5 px-3">Vigencia</th>
                  <th className="py-2.5 px-3 text-right">Total</th>
                  <th className="py-2.5 px-3 w-32">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && [1, 2, 3].map(i => (
                  <tr key={i} className="border-t border-border/60">
                    {Array(7).fill(0).map((_, j) => (
                      <td key={j} className="py-2.5 px-3"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))}
                {!isLoading && cotizaciones.map((c) => (
                  <tr key={c.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`cot-row-${c.id}`}>
                    <td className="py-2.5 px-3">
                      <Link
                        to={`/ots-comercial/cotizaciones/${c.id}`}
                        className="font-mono font-semibold text-primary hover:underline"
                        data-testid={`cot-link-${c.folio}`}
                      >
                        {c.folio}
                      </Link>
                    </td>
                    <td className="py-2.5 px-3 font-medium">{c.cliente}</td>
                    <td className="py-2.5 px-3 text-muted-foreground">{c.contacto || "-"}</td>
                    <td className="py-2.5 px-3 font-mono tabular-nums text-xs">{c.fecha || "-"}</td>
                    <td className="py-2.5 px-3 text-xs">{c.vigencia || "-"}</td>
                    <td className="py-2.5 px-3 text-right font-mono tabular-nums font-semibold text-primary">
                      {fmtMoney(c.total)}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" asChild data-testid={`cot-view-${c.id}`}>
                          <Link to={`/ots-comercial/cotizaciones/${c.id}`}><Eye className="h-3.5 w-3.5" /></Link>
                        </Button>
                        {canExport && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => downloadPdf(c.id, c.folio)} data-testid={`cot-pdf-${c.id}`}>
                            <FileText className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`cot-delete-trigger-${c.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar cotización</AlertDialogTitle>
                                <AlertDialogDescription>
                                  ¿Estás seguro de eliminar la cotización <strong>{c.folio}</strong>? Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(c.id)} data-testid={`cot-delete-confirm-${c.id}`}>
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!isLoading && cotizaciones.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-muted-foreground">
                      <Calculator className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      {search ? "Sin resultados para la búsqueda" : "Sin cotizaciones. Crea la primera."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
